import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { func, number, object } from 'prop-types';
import { useSlideNavigation } from './hooks/use-slide-navigation.js';
import styles from './style.module.css';
import { is } from './lib/is.js';
import { useSchemaStyle } from './hooks/use-schema-style.js';
import { useClickListener } from './hooks/use-internal-clicks.js';
import { useCloseKeys } from './hooks/use-close-keys.js';
import { scopeLogger } from './lib/scope-logger.js';
import Bullets from './components/bullets/bullets.jsx';
import { APIContext } from './contexts/api-context.js';

DisplayCreative.propTypes = {
	creative: object,
	onRequestClose: func,
	onRequestAction: func,
	onRequestExclusive: func,
	entrySlide: number,
};

const console = scopeLogger('RMI:Promo:display-creative');

export default function DisplayCreative (props) {
	const {
		creative: schema,
		entrySlide = 0,
		onRequestClose: closeCreative,
		onRequestAction: requestAction,
		onRequestExclusive: requestExclusiveView,
	} = props;
	/**
	 * @type {React.MutableRefObject<HTMLDivElement>}
	 */
	const ref = useRef();

	useEffect(() => {
		if (!schema) {
			return;
		}
		schema.dispatch.show();

		return schema.dispatch.close;
	}, [ schema ]);

	const bullets = useMemo(() => schema?.bullets ?? { total: 0 }, [ schema ]);
	const slides = useMemo(() => schema?.slides ?? [], [ schema ]);
	const [ currentSlideIndex, requestSlideNavigation ] = useSlideNavigation(entrySlide, schema);
	const currentSlide = useMemo(() => {
		console.log(`activate ${currentSlideIndex} slide`, slides[currentSlideIndex]);
		return slides[currentSlideIndex];
	}, [ slides, currentSlideIndex ]);

	const externalAction = useRef(Function.prototype);
	const clickListener = useCallback((event) => {
		if (event.detail.isMainTarget){
			schema.dispatch?.click?.();
		}

		const link = event.detail?.link;
		if (link === undefined) {
			console.warn('unexpected usage internal click', event);
			return;
		}
		const handlers = [
			[ /megogo:\/\/nextSlide/, (_navEvent, _matches) => requestSlideNavigation('next') ],
			[ /megogo:\/\/slide\/(?<target>(.+))/, (_navEvent, matches) => requestSlideNavigation(matches.groups.target) ],
			[ /megogo:\/\/close/, () => requestSlideNavigation('null') ],
			[ /megogo:\/\/(?<command>[^/]+)(?:\/(?<parameter>.+))*/, (deepLinkEvent) => {
				externalAction.current = () => requestAction(deepLinkEvent.detail);
				requestSlideNavigation('null');
			} ]
		];

		for (const [ pattern, handler ] of handlers) {
			const matches = pattern.exec(link);
			if (matches) {
				handler(event, matches);
				return;
			}
		}
		// Consider link as external, correct URL. Pass it to host-app without any checks or fallbacks.
		requestAction(event.detail);
	}, [ requestSlideNavigation ]);
	useClickListener(ref, clickListener);

	useCloseKeys(ref, () => {
		requestSlideNavigation('null');
	});

	useSchemaStyle(schema);

	const fullscreenMode = useRef(false);
	const API = useContext(APIContext);

	useEffect(() => {
		return () => {
			if (fullscreenMode.current) {
				API.hostApp.releaseForeground();
			}
		};
	}, []);

	useEffect(() => {
		const target = ref.current;
		const listener = (ev) => {
			const slide = ev.detail.origin.target;
			const isFullscreen = is.slideDisplayMode.fullscreen(slide);
			if (isFullscreen !== fullscreenMode.current) {
				if (isFullscreen) {
					API.hostApp.requestForeground({ mode: slide._displayZone });
				} else {
					API.hostApp.releaseForeground();
				}
				fullscreenMode.current = isFullscreen;
			}
		};

		target.addEventListener('slide-view', listener);

		return () => {
			target.removeEventListener('slide-view', listener);
		};
	}, []);

	useEffect(() => {
		if (!currentSlide) {
			return;
		}
		if (is.slideDisplayMode.fullscreen(currentSlide)) {
			requestExclusiveView(true);

			return () => {
				requestExclusiveView(false);
			};
		}
	}, [ currentSlide ]);

	useEffect(() => {
		const target = ref.current;
		const listener = (event) => {
			if (currentSlideIndex === null) {
				externalAction.current();
				closeCreative(event);
			}
		};

		target.addEventListener('slide-hide', listener, { passive: true });

		return () => {
			target.removeEventListener('slide-hide', listener, { passive: true });
		};
	}, [ currentSlideIndex ]);

	return (
		<>
			<div className={styles.slider} ref={ref} id={schema?.id}>
				{
					slides.map((slide) => (
						<slide.type
							key={slide.id}
							slide={slide}
							visible={slide === currentSlide}
						/>
					))
				}
			</div>
			<Bullets
				total={bullets.total}
				align={bullets.align}
				currentSlide={currentSlide}
			/>
		</>
	);
}
